import { FC } from 'react';

import { styled } from '../theme';
import Button, { ButtonProps } from './Button';

type Props = ButtonProps;

const BaseButton = styled(Button)<Props>(({ theme, disabled }) => ({
    width: '100%',
    fontWeight: theme.typography.fontWeight.medium,
    fontSize: '1.6rem',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        background: theme.color.disabled,
        boxShadow: 'unset',
    }),
}));

const PrimaryButton = styled(BaseButton)<Props>(({ theme, disabled }) => ({
    color: theme.color.white,
    background: theme.color.primary1,
    ':hover': {
        background: theme.color.primary2,
    },
    ':active': {
        background: theme.color.primary3,
    },
    ...(disabled && {
        background: theme.color.disabled,
    }),
}));

const SecondaryButton = styled(BaseButton)<Props>(({ theme, disabled }) => ({
    color: theme.color.gray70,
    background: theme.color.secondary1,
    ':hover': {
        background: theme.color.secondary2,
    },
    ':active': {
        background: theme.color.secondary3,
    },
    ...(disabled && {
        background: theme.color.disabled,
    }),
}));

const DangerButton = styled(BaseButton)<Props>(({ theme, disabled }) => ({
    color: theme.color.white,
    background: theme.color.red,
    ':hover': {
        background: theme.color.danger2,
    },
    ':active': {
        background: theme.color.danger3,
    },
    ...(disabled && {
        background: theme.color.disabled,
    }),
}));

const ModalButton: FC<Props> = ({ children, color = 'primary', type = 'button', ...props }) => {
    const StyledButton =
        color === 'primary'
            ? PrimaryButton
            : color === 'secondary'
              ? SecondaryButton
              : DangerButton;

    return (
        <StyledButton type={type} {...props}>
            {children}
        </StyledButton>
    );
};

export default ModalButton;
